import type { AxiosInstance } from 'axios';
import { v4 as uuidv4 } from 'uuid';

import type { IStorageStrategy } from '@sine/sine-browser-storage';

import DeviceTokenService from './device.token.service';

interface ICurrentTeamListener {
  id: string;
  func: (currentDeviceToken?: string) => void;
}

export default class DeviceService {
  private currentDeviceTokenListeners: Array<ICurrentTeamListener>;
  private currentDeviceToken?: string;
  private axiosInstance: AxiosInstance;

  private deviceTokenService: DeviceTokenService;

  public constructor(tokenPersistence: IStorageStrategy, axiosInstance: AxiosInstance) {
    this.deviceTokenService = new DeviceTokenService(tokenPersistence);
    this.axiosInstance = axiosInstance;
    this.currentDeviceTokenListeners = [];
  }

  public async register() {
    const storedDeviceToken = this.deviceTokenService.getToken();

    if (storedDeviceToken) {
      return storedDeviceToken;
    }

    let newDeviceToken;
    if (storedDeviceToken) {
      newDeviceToken = storedDeviceToken;
    } else {
      const registerResponse = await this.axiosInstance.post(
        `${window.env.REACT_APP_SINE_ID_API_URI}/devices/register`,
        {
          type: 'SineWeb',
          platform: 'Web',
        },
      );
      newDeviceToken = registerResponse.data.token;
    }

    this.updateDeviceToken(newDeviceToken);
  }

  private updateDeviceToken(newDeviceToken: string | undefined) {
    this.currentDeviceToken = newDeviceToken;
    if (newDeviceToken) {
      this.deviceTokenService.setToken(newDeviceToken);
    } else {
      this.deviceTokenService.clearToken();
    }
    this.currentDeviceTokenListeners.forEach(({ func }) => func(newDeviceToken));
  }

  public getDeviceToken() {
    return this.deviceTokenService.getToken() ?? this.currentDeviceToken;
  }

  public addDeviceTokenListener(func: (currentDeviceToken?: string) => void): string {
    const id = uuidv4();
    this.currentDeviceTokenListeners.push({
      func,
      id,
    });
    return id;
  }

  public removeDeviceTokenListener(id: string) {
    this.currentDeviceTokenListeners = this.currentDeviceTokenListeners.filter((listener) => listener.id !== id);
  }

  public trashDeviceToken() {
    this.updateDeviceToken(undefined);
  }
}
